import { useCallback, useEffect, useRef, useState } from 'react';
export default function useConnectionDetails(agentId, mode, storeId) {
    // Generate room connection details, including:
    //   - A random Room name
    //   - A random Participant name
    //   - An Access Token to permit the participant to join the room
    //   - The URL of the LiveKit server to connect to
    //
    // In real-world application, you would likely allow the user to specify their
    // own participant name, and possibly to choose from existing rooms to join.
    //
    // If storeId is provided, it takes precedence over agentId for room naming.
    // This allows the Python backend to look up the agent by storeId dynamically.
    const [connectionDetails, setConnectionDetails] = useState(null);
    const [error, setError] = useState(null);
    const inFlightRef = useRef(false);
    const lastCallAtRef = useRef(0);
    const fetchConnectionDetails = useCallback(() => {
        var _a;
        // Coalesce rapid successive calls and avoid concurrent fetches
        const now = Date.now();
        if (inFlightRef.current || now - lastCallAtRef.current < 250) {
            return;
        }
        inFlightRef.current = true;
        setConnectionDetails(null);
        setError(null);
        const endpoint = (_a = process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT) !== null && _a !== void 0 ? _a : '/api/connection-details';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const embedBase = window.__LK_EMBED_BASE__;
        const base = embedBase || window.location.origin;
        const url = endpoint.startsWith('http') ? new URL(endpoint) : new URL(endpoint, base);
        // If storeId is provided, use it (takes precedence for room naming)
        // Otherwise fall back to agentId
        if (storeId) {
            url.searchParams.set('storeId', storeId);
        }
        else if (agentId) {
            url.searchParams.set('agentId', agentId);
        }
        if (mode === 'chat' || mode === 'voice') {
            url.searchParams.set('mode', mode);
        }
        fetch(url.toString())
            .then((res) => {
            if (!res.ok) {
                return res
                    .json()
                    .catch(() => ({ error: `Token endpoint responded ${res.status}` }))
                    .then((body) => {
                    const msg = typeof (body === null || body === void 0 ? void 0 : body.error) === 'string' ? body.error : `Token endpoint responded ${res.status}`;
                    throw new Error(msg);
                });
            }
            return res.json();
        })
            .then((data) => {
            setConnectionDetails(data);
        })
            .catch((error) => {
            console.error('Error fetching connection details:', error);
            setError(error instanceof Error ? error.message : 'Failed to get connection details');
        })
            .finally(() => {
            inFlightRef.current = false;
            lastCallAtRef.current = Date.now();
        });
    }, [agentId, mode, storeId]);
    useEffect(() => {
        fetchConnectionDetails();
    }, [fetchConnectionDetails]);
    return { connectionDetails, error, refreshConnectionDetails: fetchConnectionDetails };
}
